import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import * as authApi from './authApi.js';
import { setAccessToken, setRefreshHandler, triggerRefresh } from './authBridge.js';

export interface AuthUser {
  readonly id: string;
  readonly username: string;
  readonly displayName: string;
  readonly roles: readonly string[];
}

export interface AuthState {
  readonly user: AuthUser | null;
  readonly token: string | null;
  readonly refreshToken: string | null;
  readonly mfaChallengeId: string | null;
  readonly mfaMethod: string | null;
}

export interface AuthContextValue extends AuthState {
  readonly login: (username: string, password: string, tenantCode?: string) => Promise<{ requiresMfa: boolean }>;
  readonly verifyMfa: (code: string) => Promise<void>;
  readonly requestOtp: (identifier: string, method: authApi.OtpAuthMethod, tenantCode?: string) => Promise<authApi.OtpRequestResult>;
  readonly verifyOtp: (otpToken: string, code: string) => Promise<void>;
  readonly forgot: (username: string) => Promise<{ resetTokenIssued: true }>;
  readonly logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY = 'tradelogic.admin.auth.v1';

/**
 * SEC-001 — Only NON-SENSITIVE user metadata is persisted.
 *
 * Before: localStorage held { user, token, refreshToken, mfaChallengeId, mfaMethod }.
 * After:  localStorage holds { user, mfaChallengeId, mfaMethod } only.
 *
 * - `token` (access JWT) lives in memory only (authBridge). Lost on tab
 *   close; recovered on next API call via the 401-refresh interceptor.
 * - `refreshToken` lives in an httpOnly cookie set by the backend; it is
 *   invisible to JavaScript so XSS can no longer exfiltrate it.
 */
interface PersistedAuth {
  readonly user: AuthUser | null;
  readonly mfaChallengeId: string | null;
  readonly mfaMethod: string | null;
}

function readStored(): PersistedAuth | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedAuth & AuthState>;
    return {
      user: parsed.user ?? null,
      mfaChallengeId: parsed.mfaChallengeId ?? null,
      mfaMethod: parsed.mfaMethod ?? null,
    };
  } catch {
    return null;
  }
}

function writeStored(state: AuthState): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (!state.user && !state.mfaChallengeId) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      // SEC-001: tokens NEVER hit storage.
      const persisted: PersistedAuth = {
        user: state.user,
        mfaChallengeId: state.mfaChallengeId,
        mfaMethod: state.mfaMethod,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
    }
  } catch { /* ignore quota / disabled storage */ }
}

export interface AuthProviderProps {
  readonly children: ReactNode;
  /** Test-only injection of initial state. */
  readonly initialState?: Partial<AuthState>;
}

export function AuthProvider({ children, initialState }: AuthProviderProps): ReactNode {
  const stored = initialState ? null : readStored();
  const [state, setState] = useState<AuthState>({
    user: initialState?.user ?? stored?.user ?? null,
    // SEC-001: tokens never persisted. They live in memory only and are
    // restored on first API call via the 401-refresh interceptor.
    token: initialState?.token ?? null,
    refreshToken: initialState?.refreshToken ?? null,
    mfaChallengeId: initialState?.mfaChallengeId ?? stored?.mfaChallengeId ?? null,
    mfaMethod: initialState?.mfaMethod ?? stored?.mfaMethod ?? null,
  });

  useEffect(() => { writeStored(state); }, [state]);

  // Keep the api-client bridge in sync so /api/client.ts can read the
  // current access token + trigger a refresh on 401 without importing
  // React context (INT-012).
  useEffect(() => { setAccessToken(state.token); }, [state.token]);
  useEffect(() => {
    // SEC-001: refresh handler no longer carries the refresh token —
    // the httpOnly cookie is attached automatically by the browser when
    // api/client.ts hits /auth/refresh with credentials:'include'.
    const handler = async (): Promise<string | null> => {
      const res = await authApi.refreshWithReason();
      if (!res.ok) {
        // A rejected credential means the session is over: clear it so
        // RequireAuth routes to sign-in. Without this the app keeps a
        // tokenless "signed in" state and the sidebar waits forever.
        // A transient failure must NOT sign the user out.
        setState((s) =>
          res.authFailed || !s.user
            ? {
                user: null,
                token: null,
                refreshToken: null,
                mfaChallengeId: null,
                mfaMethod: null,
              }
            : { ...s, token: null },
        );
        return null;
      }
      setState((s) => ({
        ...s,
        token: res.value.token,
        refreshToken: null,
        user: res.value.user ?? s.user,
      }));
      return res.value.token;
    };
    setRefreshHandler(handler);
    return () => { setRefreshHandler(null); };
  }, []);

  // SEC-001 bootstrap: the access token is memory-only, so a hard reload
  // (or a fresh tab) starts with `token === null` even though the user is
  // still authenticated (their `refresh_token` httpOnly cookie is valid).
  // Silently restore the access token once on mount so the menu and every
  // data page work immediately instead of 401-ing until the first manual
  // action. If the refresh fails (no/expired cookie) we clear auth so the
  // router redirects to /login.
  const bootstrappedRef = useRef(false);
  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    if (!(state.user && !state.token)) return;
    // Route the bootstrap refresh through the shared in-flight dedup
    // (authBridge.triggerRefresh) instead of calling authApi.refresh()
    // directly. The backend ROTATES (invalidates) the refresh token on
    // every POST /v1/auth/refresh, so if this bootstrap refresh and a
    // concurrent 401-driven refresh (the menu + data pages mounting at the
    // same time on a hard reload) both present the SAME `refresh_token`
    // cookie, one call rotates it and the other finds no session for the
    // now-stale hash → 401 EX-AUT-05. That loser's failure path clears auth
    // and bounces an otherwise-authenticated user to /login — the "data
    // comes back in the Network tab but the page shows nothing / redirects
    // to login on reload" symptom. triggerRefresh() collapses every
    // concurrent refresh into ONE request; the registered handler performs
    // the setState (token restore on success, clear on genuine failure).
    //
    // NOTE: intentionally NO cancel-on-unmount guard. Under React 18
    // StrictMode (dev) the provider is mounted, unmounted, then remounted;
    // a cancel-in-cleanup flag would abort this single in-flight refresh
    // while the bootstrappedRef guard blocks the remount from retrying,
    // leaving `token === null` forever. The refresh + setState is idempotent
    // and always desirable, and the handler's setState is stable across the
    // StrictMode remount.
    void triggerRefresh();
    // Mount-only: uses the initial persisted state; guarded by bootstrappedRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (username: string, password: string, tenantCode?: string) => {
    const res = await authApi.login(username, password, tenantCode);
    if (res.requiresMfa) {
      setState((s) => ({
        ...s,
        mfaChallengeId: res.mfaChallengeId,
        mfaMethod: res.mfaMethod,
      }));
      return { requiresMfa: true };
    }
    setState({
      user: res.user,
      token: res.token,
      refreshToken: null, // SEC-001: lives in httpOnly cookie
      mfaChallengeId: null,
      mfaMethod: null,
    });
    return { requiresMfa: false };
  }, []);

  const verifyMfa = useCallback(
    async (code: string) => {
      const challengeId = state.mfaChallengeId;
      if (!challengeId) throw new Error('No MFA challenge in progress');
      const res = await authApi.verifyMfa(challengeId, code, state.mfaMethod);
      setState({
        user: res.user,
        token: res.token,
        refreshToken: null, // SEC-001: lives in httpOnly cookie
        mfaChallengeId: null,
        mfaMethod: null,
      });
    },
    [state.mfaChallengeId, state.mfaMethod],
  );

  const forgot = useCallback(async (username: string) => {
    return authApi.forgot(username);
  }, []);

  const requestOtp = useCallback(
    async (identifier: string, method: authApi.OtpAuthMethod, tenantCode?: string) => {
      return authApi.requestOtp(identifier, method, tenantCode);
    },
    [],
  );

  const verifyOtp = useCallback(async (otpToken: string, code: string) => {
    const res = await authApi.verifyOtp(otpToken, code);
    setState({
      user: res.user,
      token: res.token,
      refreshToken: null, // SEC-001: lives in httpOnly cookie
      mfaChallengeId: null,
      mfaMethod: null,
    });
  }, []);

  const logout = useCallback(() => {
    // Fire-and-forget backend logout; clear local state immediately.
    void authApi.logout();
    setState({ user: null, token: null, refreshToken: null, mfaChallengeId: null, mfaMethod: null });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, verifyMfa, requestOtp, verifyOtp, forgot, logout }),
    [state, login, verifyMfa, requestOtp, verifyOtp, forgot, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
